/**
 * LiveActivityStatus - the live status line layer of the chat Activity_Indicator.
 *
 * While the assistant response is still streaming, this renders exactly one
 * `aria-live="polite"` status line showing the label of the most-recently-started
 * in-progress activity (Requirements 7.1, 7.2, 13.1, 13.2). When no activity is
 * in progress it renders nothing, so the line clears once every activity reaches
 * a terminal status or the stream ends (Requirement 7.3).
 *
 * The activity type is conveyed through visible text (the presentation label),
 * never through color alone (Requirement 13.7).
 */
import { clsx } from 'clsx';
import type { Activity } from '@/types/chat';
import { getActivityPresentation, selectLiveActivity } from './activityPresentation';

export interface LiveActivityStatusProps {
  /** The message's Activity_Trace. */
  activities: Activity[];
  /** Optional extra classes for layout. */
  className?: string;
}

/**
 * Render the single live status line for the most-recently-started in-progress
 * activity, or nothing when none is in progress.
 */
export function LiveActivityStatus({ activities, className }: LiveActivityStatusProps) {
  const live = selectLiveActivity(activities);

  if (!live) {
    return null;
  }

  const { label } = getActivityPresentation(live);

  return (
    <div
      aria-live="polite"
      className={clsx('flex items-center gap-2 text-xs text-dark-400', className)}
      data-testid="live-activity-status"
    >
      {/* Animated indicator, decorative only (type is conveyed by the text below). */}
      <span
        className="flex items-center gap-0.5"
        aria-hidden="true"
      >
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-dark-400 [animation-delay:0ms]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-dark-400 [animation-delay:150ms]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-dark-400 [animation-delay:300ms]" />
      </span>
      <span className="truncate">{label}</span>
    </div>
  );
}
